import React, { useState, useEffect } from 'react';
import LicenseService from '../services/licenseService';
import { Shield, Users, Calendar, Key, Plus, Edit, Trash, Eye } from 'lucide-react';

const AdminPanel = () => {
    const [licenses, setLicenses] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [showEditForm, setShowEditForm] = useState(false);

    // Form states
    const [formData, setFormData] = useState({
        expiryDate: '',
        maxUsers: 1,
        notes: ''
    });

    // Edit form states
    const [editFormData, setEditFormData] = useState({
        id: null,
        expiryDate: '',
        maxUsers: 1,
        notes: ''
    });

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            setError(null);
            setSuccess(null);

            // Kiểm tra token trước
            const token = localStorage.getItem('token');
            if (!token) {
                setError('Không có token xác thực. Vui lòng đăng nhập lại.');
                return;
            }

            // Load licenses
            const result = await LicenseService.getAllLicenses();

            if (result.success) {
                // Đảm bảo licenses luôn là array
                if (result.data && Array.isArray(result.data)) {
                    setLicenses(result.data);
                } else {
                    setLicenses([]);
                }
            } else {
                setError(result.error || 'Không thể tải danh sách license.');
                setLicenses([]);
            }

            // Load users - tạm thời bỏ qua vì API chưa có
            setUsers([]);

        } catch (error) {
            console.error('Error loading data:', error);
            setError('Không thể tải dữ liệu. Vui lòng kiểm tra kết nối mạng.');
            setLicenses([]);
            setUsers([]);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateLicense = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const token = localStorage.getItem('token');
            if (!token) {
                setError('Không có token xác thực');
                setLoading(false);
                return;
            }

            const result = await LicenseService.createLicense({
                expiryDate: formData.expiryDate,
                maxUsers: formData.maxUsers,
                notes: formData.notes
            });

            if (result.success) {
                setShowCreateForm(false);
                setFormData({ expiryDate: '', maxUsers: 1, notes: '' });
                setError(null);
                setSuccess('Tạo license thành công!');
                // Auto hide success message after 3 seconds
                setTimeout(() => setSuccess(null), 3000);
                // Reload data to get the latest list
                await loadData();
            } else {
                setError(result.error || 'Không thể tạo license');
                setSuccess(null);
            }
        } catch (error) {
            console.error('Error in handleCreateLicense:', error);
            setError('Lỗi kết nối: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteLicense = async (licenseId) => {
        if (!window.confirm('Bạn có chắc chắn muốn xóa license này không?')) {
            return;
        }
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                setError('Không có token xác thực. Vui lòng đăng nhập lại.');
                return;
            }

            const result = await LicenseService.deleteLicense(licenseId);

            if (result.success) {
                await loadData();
                setError(null);
                setSuccess('Xóa license thành công!');
                // Auto hide success message after 3 seconds
                setTimeout(() => setSuccess(null), 3000);
            } else {
                setError(result.error || 'Không thể xóa license');
                setSuccess(null);
            }
        } catch (error) {
            console.error('Error deleting license:', error);
            setError('Đã xảy ra lỗi khi xóa license');
        }
    };

    const handleEditLicense = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const token = localStorage.getItem('token');
            if (!token) {
                setError('Không có token xác thực');
                setLoading(false);
                return;
            }

            const result = await LicenseService.updateLicense(editFormData.id, {
                expiryDate: editFormData.expiryDate,
                maxUsers: editFormData.maxUsers,
                notes: editFormData.notes
            });

            if (result.success) {
                await loadData();
                setShowEditForm(false);
                setEditFormData({ id: null, expiryDate: '', maxUsers: 1, notes: '' });
                setError(null);
                setSuccess('Cập nhật license thành công!');
                // Auto hide success message after 3 seconds
                setTimeout(() => setSuccess(null), 3000);
            } else {
                setError(result.error || 'Không thể cập nhật license');
                setSuccess(null);
            }
        } catch (error) {
            setError('Lỗi kết nối: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const openEditForm = (license) => {
        setEditFormData({
            id: license.id,
            expiryDate: license.expiryDate.split('T')[0], // Format date for input
            maxUsers: license.maxUsers,
            notes: license.notes || '' // Set notes for editing
        });
        setShowEditForm(true);
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('vi-VN');
    };

    const getStatusBadge = (status) => {
        const statusClasses = {
            active: 'bg-green-100 text-green-800',
            expired: 'bg-red-100 text-red-800',
            suspended: 'bg-yellow-100 text-yellow-800',
            revoked: 'bg-gray-100 text-gray-800'
        };

        return (
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusClasses[status] || 'bg-gray-100 text-gray-800'}`}>
                {status === 'active' ? 'Hoạt động' :
                    status === 'expired' ? 'Hết hạn' :
                        status === 'suspended' ? 'Tạm ngưng' :
                            status === 'revoked' ? 'Đã thu hồi' : status}
            </span>
        );
    };

    const getLicenseTypeBadge = (type) => {
        const typeClasses = {
            trial: 'bg-blue-100 text-blue-800',
            basic: 'bg-green-100 text-green-800',
            premium: 'bg-purple-100 text-purple-800',
            enterprise: 'bg-orange-100 text-orange-800'
        };

        return (
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${typeClasses[type] || 'bg-gray-100 text-gray-800'}`}>
                {type === 'trial' ? 'Dùng thử' :
                    type === 'basic' ? 'Cơ bản' :
                        type === 'premium' ? 'Cao cấp' :
                            type === 'enterprise' ? 'Doanh nghiệp' : type}
            </span>
        );
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center space-x-3 mb-4">
                        <Shield className="h-8 w-8 text-blue-600" />
                        <h1 className="text-3xl font-bold text-gray-900">Quản lý License</h1>
                    </div>
                    <p className="text-gray-600">Quản lý license và quyền truy cập của người dùng</p>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="flex items-center">
                            <div className="p-2 bg-blue-100 rounded-lg">
                                <Key className="h-6 w-6 text-blue-600" />
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-600">Tổng License</p>
                                <p className="text-2xl font-bold text-gray-900">{licenses.length}</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="flex items-center">
                            <div className="p-2 bg-green-100 rounded-lg">
                                <Users className="h-6 w-6 text-green-600" />
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-600">Đang hoạt động</p>
                                <p className="text-2xl font-bold text-gray-900">
                                    {licenses.filter(l => l.status === 'active').length}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="flex items-center">
                            <div className="p-2 bg-red-100 rounded-lg">
                                <Calendar className="h-6 w-6 text-red-600" />
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-600">Hết hạn</p>
                                <p className="text-2xl font-bold text-gray-900">
                                    {licenses.filter(l => l.status === 'expired').length}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="flex items-center">
                            <div className="p-2 bg-purple-100 rounded-lg">
                                <Users className="h-6 w-6 text-purple-600" />
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-600">Tổng người dùng</p>
                                <p className="text-2xl font-bold text-gray-900">
                                    {licenses.reduce((total, license) => total + (license.users ? license.users.length : 0), 0)}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="flex items-center">
                            <div className="p-2 bg-yellow-100 rounded-lg">
                                <Users className="h-6 w-6 text-yellow-600" />
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-600">Tạm ngưng</p>
                                <p className="text-2xl font-bold text-gray-900">
                                    {licenses.filter(l => l.status === 'suspended').length}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>


                {/* Action Buttons */}
                <div className="mb-6 flex justify-between items-center">
                    <h2 className="text-xl font-semibold text-gray-900">Danh sách License</h2>
                    <div className="flex space-x-2">
                        <button
                            onClick={loadData}
                            className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
                        >
                            <span>Refresh</span>
                        </button>
                        <button
                            onClick={() => setShowCreateForm(true)}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
                        >
                            <Plus className="h-4 w-4" />
                            <span>Tạo License mới</span>
                        </button>
                    </div>
                </div>

                {/* Error Message */}
                {error && (
                    <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
                        <p className="text-red-800">{error}</p>
                    </div>
                )}

                {/* Success Message */}
                {success && (
                    <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
                        <p className="text-green-800">{success}</p>
                    </div>
                )}

                {/* Licenses Table */}
                <div className="bg-white shadow rounded-lg overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Mã License
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Ngày hết hạn
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Số người dùng
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Ghi chú
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Trạng thái
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Số người dùng
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Thao tác
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">

                                {licenses.length === 0 ? (
                                    <tr>
                                        <td colSpan="7" className="px-6 py-4 text-center text-sm text-gray-500">
                                            Không có license nào
                                        </td>
                                    </tr>
                                ) : (
                                    licenses.map((license, index) => {
                                        return (
                                            <tr key={license.id}>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                                                    {license.licenseKey}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    {new Date(license.expiryDate).toLocaleDateString('vi-VN')}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    {license.maxUsers} người
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    {license.notes || 'Không có ghi chú'}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${license.status === 'active'
                                                        ? 'bg-green-100 text-green-800'
                                                        : 'bg-red-100 text-red-800'
                                                        }`}>
                                                        {license.status === 'active' ? 'Hoạt động' : 'Không hoạt động'}
                                                    </span>
                                                </td>
                                                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {license.users && license.users.length > 0 ? (
                                                <span className="font-medium">
                                                    {license.users.length}/{license.maxUsers} người dùng
                                                </span>
                                            ) : (
                                                <span className="text-gray-500">0/{license.maxUsers} người dùng</span>
                                            )}
                                        </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                    <div className="flex space-x-2">
                                                        <button
                                                            onClick={() => openEditForm(license)}
                                                            className="text-blue-600 hover:text-blue-900"
                                                        >
                                                            Chỉnh sửa
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteLicense(license.id)}
                                                            className="text-red-600 hover:text-red-900"
                                                        >
                                                            Xóa
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Create License Modal */}
                {showCreateForm && (
                    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                        <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
                            <div className="mt-3">
                                <h3 className="text-lg font-medium text-gray-900 mb-4">Tạo License mới</h3>
                                <form onSubmit={handleCreateLicense}>
                                    <div className="mb-4">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Ngày hết hạn
                                        </label>
                                        <input
                                            type="date"
                                            value={formData.expiryDate}
                                            onChange={(e) => setFormData({ ...formData, expiryDate: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            required
                                        />
                                    </div>

                                    <div className="mb-4">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Số người dùng tối đa
                                        </label>
                                        <input
                                            type="number"
                                            min="1"
                                            value={formData.maxUsers}
                                            onChange={(e) => setFormData({ ...formData, maxUsers: parseInt(e.target.value) || 1 })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>

                                    <div className="mb-4">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Ghi chú
                                        </label>
                                        <textarea
                                            value={formData.notes}
                                            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            rows="3"
                                        />
                                    </div>

                                    <div className="flex justify-end space-x-3">
                                        <button
                                            type="button"
                                            onClick={() => setShowCreateForm(false)}
                                            className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
                                        >
                                            Hủy
                                        </button>
                                        <button
                                            type="submit"
                                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                                        >
                                            Tạo License
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                )}

                {/* Edit License Modal */}
                {showEditForm && (
                    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                        <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
                            <div className="mt-3">
                                <h3 className="text-lg font-medium text-gray-900 mb-4">Chỉnh sửa License</h3>
                                <form onSubmit={handleEditLicense}>
                                    <div className="mb-4">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Ngày hết hạn
                                        </label>
                                        <input
                                            type="date"
                                            value={editFormData.expiryDate}
                                            onChange={(e) => setEditFormData({ ...editFormData, expiryDate: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            required
                                        />
                                    </div>

                                    <div className="mb-4">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Số người dùng tối đa
                                        </label>
                                        <input
                                            type="number"
                                            min="1"
                                            value={editFormData.maxUsers}
                                            onChange={(e) => setEditFormData({ ...editFormData, maxUsers: parseInt(e.target.value) || 1 })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>

                                    <div className="mb-4">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Ghi chú
                                        </label>
                                        <textarea
                                            value={editFormData.notes}
                                            onChange={(e) => setEditFormData({ ...editFormData, notes: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            rows="3"
                                        />
                                    </div>

                                    <div className="flex justify-end space-x-3">
                                        <button
                                            type="button"
                                            onClick={() => setShowEditForm(false)}
                                            className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
                                        >
                                            Hủy
                                        </button>
                                        <button
                                            type="submit"
                                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                                        >
                                            Cập nhật
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminPanel;
